import { useMemo, useState } from 'react';
import { Pencil, Plus, RotateCcw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES, type UserRole } from '@/lib/constants/roles';
import { 
  DASHBOARD_TABS, 
  type TabId, 
  type WidgetId, 
} from '../config';
import { useDashboardLayout } from '../use-dashboard-layout';
import { AddWidgetSheet } from './AddWidgetSheet';
import { KpiStrip } from './KpiStrip';
import { WidgetGrid } from './WidgetGrid';
import { FlashBanner } from '@/features/flash-messages/pages/FlashBanner';
import { useCurrentUserModules } from '@/lib/permissions/use-current-modules';

function useEffectiveRole(): UserRole {
  const role = useAuthStore((s) => s.role) as UserRole;
  const isSubSuper = useAuthStore((s) => s.isSubSuperAdmin());
  return isSubSuper ? USER_ROLES.SUB_SUPER_ADMIN : role;
}

export function DashboardShell() {
  const role = useEffectiveRole();
  // Inbound tab only if the user has the Inbound module (id 6).
  const { modules } = useCurrentUserModules();
  const hasInbound = modules === null || modules.has(6);
  const visibleTabs = useMemo(
    () =>
      DASHBOARD_TABS.filter(
        (t) => t.roles.includes(role) && (t.id !== 'inbound' || hasInbound),
      ),
    [role, hasInbound],
  );
  const [activeTab, setActiveTab] = useState<TabId>(
    visibleTabs[0]?.id ?? 'overview',
  );
  const [editing, setEditing] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  const { layout, reorder, setSize, add, remove, reset } = 
  useDashboardLayout(activeTab);

  const switchTab = (next: TabId) => {
    setActiveTab(next);
    setEditing(false);
    setAddOpen(false);
  };

  const stopEditing = () => {
    setEditing(false);
    setAddOpen(false);
  };

  /*
   * Welcome flash banner is user-dashboard only — admins/super-admins/
   * sub-users don't get it, even though "dashboard_reseller" exists as a
   * message row (unused here; wire it up separately if a reseller-facing
   * dashboard needs its own banner later).
   */
  // const _showFlashBanner = true;

  /*
   * The dashboard wraps every section (title, KPI strip, tabs, grid) in
   * one scrollable column so the whole page scrolls as a unit. Previously
   * only the grid scrolled, which clipped tall grids on smaller screens
   * and made the KPI strip steal vertical space from the tab body.
   */
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4 lg:px-8 lg:py-6">
      <div className="flex flex-col gap-4">
        {/* Title row */}
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              Dashboard
              </h1>
            <p className="text-xs text-muted-foreground">
              Drag tiles in customize mode. Each tab's layout saves automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {editing && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { reset(); setResetKey((k) => k + 1); }}
                  title="Reset this tab to defaults"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </Button>
                <Button 
                type="button" 
                size="sm" 
                onClick={() => setAddOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add widget
                </Button>
              </>
            )}
            <Button
              type="button"
              variant={editing ? 'default' : 'outline'}
              size="sm"
              onClick={() => (editing ? stopEditing() : setEditing(true))}
            >
              <Pencil className="h-3.5 w-3.5" />
              {editing ? 'Done' : 'Customize'}
            </Button>
          </div>
        </div>

        {/* Customize banner — makes the active mode unmissable */}
        {editing && (
          <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="flex-1">
              <strong className="text-foreground">Customize mode.</strong>{' '}
              <span className="text-muted-foreground">
                Drag the grip handles to move tiles, drag corners to resize, 
                click ✕ on a tile to remove. Use{' '}
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  Add widget
                </button>{' '}
                to bring tiles in.
              </span>
            </span>
          </div>
        )}

        <FlashBanner
          moduleName="dashboard_user"
          action="view"
        />

        {/* KPI strip — same drag + resize mechanics as the tab grids. */}
        <KpiStrip key={resetKey} role={role} editing={editing} />

        {/* Tab bar */}
        <div className="flex items-center gap-1 overflow-x-auto border-b border-border">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => switchTab(t.id)}
                className={cn(
                  'relative flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors',
                  isActive 
                  ? 'text-foreground' 
                  : 'text-muted-foreground hover:text-foreground',
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-x-2 bottom-0 h-[2px] rounded-full transition-colors',
                    isActive ? 'bg-primary' : 'bg-transparent',
                  )}
                />
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div>
          <WidgetGrid
            layout={layout}
            editing={editing}
            onReorder={reorder}
            onResize={setSize}
            onRemove={remove}
            role={role}
          />
        </div>
      </div>

      {/* Widget library slide-over */}
      <AddWidgetSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        tab={activeTab}
        role={role}
        existingIds={layout.map((l) => l.id as WidgetId)}
        onAdd={(id) => {
          add(id);
          // Keep the sheet open so the user can add several in a row.
        }}
      />
    </div>
  );
}
